import React, { useState } from 'react';

import Chart from 'react-apexcharts';

import { decimalUpTo } from 'Helpers/currencyFormat';
import { formatSeparateByThousands, formatToFixedMoneyWithLocale, formatToFixedLocaleString } from 'Helpers/formatService';

import MinusCircleIcon from 'Assets/icons/minus.svg';
import PlusCircleIcon from 'Assets/icons/plus.svg';

export default function KeyMetricsRevenueSummary(props) {
    const [show, setShow] = useState(true);

    let colorPalette = ['#27B6FF', '#21005C', '#D8C2FF'];
    let seatsAvailableChartOptions = {};
    let ticketRevenueChartOptions = {};
    let sponsorRevenueChartOptions = {};
    let highestPriceChartOptions = {};

    if (Object.keys(props.summaryRevenue).length > 0) {
        const chartOptions = {
            chart: {
                fontFamily: 'var(--font-sans)',
                type: 'pie',
                width: '100%',
                height: 200,
            },
            dataLabels: {
                enabled: false,
            },
            tooltip: {
                y: {
                    formatter: function (val) {
                        return decimalUpTo(val.toFixed(0));
                    },
                },
            },
            plotOptions: {
                pie: {
                    customScale: 0.7,
                    donut: {
                        size: '60%',
                        labels: {
                            show: false,
                            value: {
                                show: false,
                            },
                        },
                    },
                    offsetY: 0,
                },
                stroke: {
                    colors: undefined,
                },
            },
            colors: colorPalette,
            title: {
                text: props.name,
                style: {
                    fontSize: '18px',
                    color: '#000061',
                },
            },
            legend: {
                show: false,
                floating: false,
                showForNullSeries: true,
                showForZeroSeries: true,
                position: 'bottom',
                height: 80,
            },
        };
        seatsAvailableChartOptions = {
            ...chartOptions,
            series: [
                props.summaryRevenue.ticketing.total_sits_available,
                props.summaryRevenue.ticketing.total_kills_holds,
                props.summaryRevenue.ticketing.forecast_number_tickets_sold,
            ],
            labels: ['Sits Available, Kills and holds', 'Forecast sold'],
        };
        ticketRevenueChartOptions = {
            ...chartOptions,
            series: props.summaryRevenue.ticketing.top_3_by_revenue.map((top) => top.revenue),
            labels: props.summaryRevenue.ticketing.top_3_by_revenue.map((top) => top.name),
        };
        sponsorRevenueChartOptions = {
            ...chartOptions,
            series: [props.summaryRevenue.sponsorship.percentage_cash_revenue, props.summaryRevenue.sponsorship.percentage_vik_revenue],
            labels: ['Cash Revenue', 'Vik Revenue'],
        };

        const barOptions = {
            chart: {
                fontFamily: 'var(--font-sans)',
                type: 'bar',
                height: '100%',
                width: '100%',
                stacked: false,
                toolbar: {
                    tools: {
                        download: false,
                        pan: false,
                        reset: false,
                        selection: false,
                        zoom: false,
                    },
                },
            },
            tooltip: {
                y: {},
            },
            colors: colorPalette,
            xaxis: {
                labels: {
                    show: false,
                },
                axisBorder: {
                    show: false,
                },
                axisTicks: {
                    show: false,
                },
            },
            yaxis: {
                opposite: false,
                axisBorder: {
                    show: false,
                },
                axisTicks: {
                    show: false,
                },
                labels: {
                    show: false,
                },
            },
            legend: {
                show: false,
            },
            plotOptions: {
                bar: {
                    columnWidth: '25%',
                    distributed: true,
                },
            },
            dataLabels: {
                enabled: false,
            },
        };
        highestPriceChartOptions = {
            ...barOptions,
            id: 'highest-price',
            series: [
                {
                    name: 'Highest Price',
                    data: [
                        ...props.summaryRevenue.ticketing.top_3_price.map((top) => top.price),
                        ...props.summaryRevenue.ticketing.bottom_3_price.map((bottom) => bottom.price),
                    ],
                },
            ],
        };
    }

    return (
        <div className="tw-border-0 tw-rounded-lg tw-bg-white tw-shadow tw-h-full">
            <div className="tw-p-2.5 tw-flex tw-flex-col tw-gap-3">
                {Object.keys(props.summaryRevenue).length > 0 && (
                    <>
                        <div className="tw-flex tw-flex-row tw-justify-between tw-pb-3">
                            <div>
                                <h3 className="tw-text-blue tw-text-2xl tw-font-semibold tw-pb-1">{$t('Revenue Summary')}</h3>
                            </div>
                            {!show && (
                                <div className={'tw-cursor-pointer'} onClick={() => setShow(true)}>
                                    <div className={'tw-h-10 tw-w-10 tw-bg-blue tw-flex tw-justify-center tw-items-center tw-rounded-full'}>
                                        <PlusCircleIcon className={'tw-h-3.5 tw-w-3.5 tw-text-white'} />
                                    </div>
                                </div>
                            )}
                            {!!show && (
                                <div className={'tw-cursor-pointer'} onClick={() => setShow(false)}>
                                    <div className={'tw-h-10 tw-w-10 tw-bg-blue tw-flex tw-justify-center tw-items-center tw-rounded-full'}>
                                        <MinusCircleIcon className={'tw-h-3.5 tw-w-3.5 tw-text-white'} />
                                    </div>
                                </div>
                            )}
                        </div>

                        {!!show && (
                            <div className={'tw-flex max-lg:tw-flex-col tw-gap-3'}>
                                <div className="tw-flex tw-w-6/12 max-lg:tw-w-full tw-flex-col tw-gap-3">
                                    <div className="tw-rounded-lg tw-border-2 tw-border-gray-200 tw-w-full">
                                        <div className="tw-p-2.5 tw-flex">
                                            <div className={'tw-flex max-lg:tw-flex-col tw-w-full tw-gap-3'}>
                                                <div
                                                    className={'tw-w-4/12 max-lg:tw-w-full'}
                                                    style={{
                                                        position: 'relative',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            position: 'absolute',
                                                            width: '40px',
                                                            height: '40px',
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                        }}
                                                    >
                                                        <img className={'img-fluid'} src="/images/key-metrics/seats.svg" />
                                                    </div>
                                                    {Object.keys(seatsAvailableChartOptions).length > 0 && (
                                                        <Chart
                                                            options={seatsAvailableChartOptions}
                                                            series={seatsAvailableChartOptions.series}
                                                            type={'donut'}
                                                            height={200}
                                                        />
                                                    )}
                                                </div>
                                                <div className={'tw-w-8/12 max-lg:tw-w-full tw-flex tw-flex-wrap'}>
                                                    <ul className={'tw-flex tw-flex-col tw-gap-1 tw-text-sm'}>
                                                        <li className={'tw-flex tw-flex-col'}>
                                                            <span className={'tw-w-full tw-font-semibold'}>{$t('Total seats available')}</span>
                                                            <span className={'tw-w-full inbud-numeric-text'}>
                                                                {formatSeparateByThousands(props.summaryRevenue.ticketing.total_sits_available)}
                                                            </span>
                                                        </li>
                                                        <li className={'tw-flex tw-flex-col'}>
                                                            <span className={'tw-w-full tw-font-semibold'}>{$t('Total kills and holds')}</span>
                                                            <span className={'tw-w-full inbud-numeric-text'}>
                                                                {formatSeparateByThousands(props.summaryRevenue.ticketing.total_kills_holds)}
                                                            </span>
                                                        </li>
                                                        <li className={'tw-flex tw-flex-col'}>
                                                            <span className={'tw-w-full tw-font-semibold'}>{$t('% kills and holds')}</span>
                                                            <span className={'tw-w-full inbud-numeric-text'}>
                                                                {formatToFixedLocaleString(props.summaryRevenue.ticketing.percentage_kills_holds)}%
                                                            </span>
                                                        </li>
                                                        <li className={'tw-flex tw-flex-col'}>
                                                            <span className={'tw-w-full tw-font-semibold'}>{$t('Forecast number of tickets sold')}</span>
                                                            <span className={'tw-w-full inbud-numeric-text'}>
                                                                {formatSeparateByThousands(props.summaryRevenue.ticketing.forecast_number_tickets_sold)}
                                                            </span>
                                                        </li>
                                                        <li className={'tw-flex tw-flex-col'}>
                                                            <span className={'tw-w-full tw-font-semibold'}>{$t('% forecast sold')}</span>
                                                            <span className={'tw-w-full inbud-numeric-text'}>
                                                                {formatToFixedLocaleString(props.summaryRevenue.ticketing.forecast_percentage_sold)}%
                                                            </span>
                                                        </li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="tw-rounded-lg tw-border-2 tw-border-gray-200 tw-w-full">
                                        <div className="tw-p-2.5 tw-flex">
                                            <div className={'tw-flex max-lg:tw-flex-col tw-w-full tw-gap-3'}>
                                                <div
                                                    className={'tw-w-4/12 max-lg:tw-w-full'}
                                                    style={{
                                                        position: 'relative',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            position: 'absolute',
                                                            width: '40px',
                                                            height: '40px',
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                        }}
                                                    >
                                                        <img className={'img-fluid'} src="/images/key-metrics/ticket.svg" />
                                                    </div>
                                                    {Object.keys(ticketRevenueChartOptions).length > 0 && (
                                                        <Chart
                                                            options={ticketRevenueChartOptions}
                                                            series={ticketRevenueChartOptions.series}
                                                            type={'donut'}
                                                            height={200}
                                                        />
                                                    )}
                                                </div>
                                                <div className={'tw-w-8/12 max-lg:tw-w-full tw-flex tw-content-center tw-flex-wrap'}>
                                                    <ul className={'tw-flex tw-flex-col tw-gap-1 tw-text-sm'}>
                                                        <li className={'tw-flex tw-flex-col'}>
                                                            <span className={'tw-w-full tw-font-semibold'}>{$t('Total Ticket Revenue')}</span>
                                                            <span className={'tw-w-full inbud-numeric-text'}>
                                                                {formatToFixedMoneyWithLocale(props.summaryRevenue.ticketing.total_revenue)}
                                                            </span>
                                                        </li>
                                                        <li className={'tw-flex tw-flex-col'}>
                                                            <span className={'tw-w-full tw-font-extrabold tw-text-blue tw-uppercase'}>{$t('Top 3')}</span>
                                                        </li>
                                                        {props.summaryRevenue.ticketing.top_3_by_revenue.map((row) => {
                                                            return (
                                                                <li key={'ticketing_top_3_by_revenue_' + row.name} className={'tw-flex tw-flex-col'}>
                                                                    <span className={'tw-w-full tw-font-semibold'}>{row.name}</span>
                                                                    <span className={'tw-w-full inbud-numeric-text'}>
                                                                        {formatToFixedMoneyWithLocale(row.revenue)}
                                                                    </span>
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="tw-flex tw-w-6/12 max-lg:tw-w-full tw-gap-3">
                                    <div className="tw-rounded-lg tw-border-2 tw-border-gray-200 tw-w-full">
                                        <div className="tw-p-2.5 tw-flex">
                                            <div className={'tw-flex max-lg:tw-flex-col tw-w-full tw-gap-3'}>
                                                <div className={'tw-w-4/12 max-lg:tw-w-full tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-h-full'}>
                                                    {Object.keys(highestPriceChartOptions).length > 0 && (
                                                        <Chart
                                                            options={highestPriceChartOptions}
                                                            series={highestPriceChartOptions.series}
                                                            type={'bar'}
                                                            height={'450px'}
                                                        />
                                                    )}
                                                </div>
                                                <div className={'tw-w-8/12 max-lg:tw-w-full tw-flex tw-content-center tw-flex-wrap'}>
                                                    <ul className={'tw-flex tw-flex-col tw-gap-1 tw-text-sm'}>
                                                        <li className={'tw-flex'}>
                                                            <span className={'tw-w-col tw-font-extrabold tw-text-blue'}>
                                                                {$t('Highest Price (Top 3)').toUpperCase()}
                                                            </span>
                                                        </li>
                                                        {props.summaryRevenue.ticketing.top_3_price.map((row) => {
                                                            return (
                                                                <li key={'ticketing_top_3_price_' + row.name} className={'tw-flex tw-flex-col'}>
                                                                    <span className={'tw-w-full tw-font-semibold'}>{row.name}</span>
                                                                    <span className={'tw-w-full inbud-numeric-text'}>
                                                                        {formatToFixedMoneyWithLocale(row.price)}
                                                                    </span>
                                                                </li>
                                                            );
                                                        })}
                                                        <li className={'tw-flex'}>
                                                            <span className={'tw-w-col tw-font-extrabold tw-text-blue'}>
                                                                {$t('Lowest Price (Bottom 3)').toUpperCase()}
                                                            </span>
                                                        </li>
                                                        {props.summaryRevenue.ticketing.bottom_3_price.map((row) => {
                                                            return (
                                                                <li key={'ticketing_bottom_3_price_' + row.name} className={'tw-flex tw-flex-col'}>
                                                                    <span className={'tw-w-full tw-font-semibold'}>{row.name}</span>
                                                                    <span className={'tw-w-full inbud-numeric-text'}>
                                                                        {formatToFixedMoneyWithLocale(row.price)}
                                                                    </span>
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {!!show && (
                            <div>
                                <h3 className="tw-text-blue tw-text-2xl tw-font-semibold tw-pb-1">{$t('Sponsorship')}</h3>
                            </div>
                        )}

                        {!!show && (
                            <div className={'tw-flex max-lg:tw-flex-col tw-gap-3'}>
                                <div className="tw-rounded-lg tw-border-2 tw-border-gray-200 tw-w-4/12 max-lg:tw-w-full">
                                    <div className="tw-p-2.5 tw-flex">
                                        <div className={'tw-flex max-lg:tw-flex-col tw-w-full tw-gap-3'}>
                                            <div
                                                className={'tw-w-4/12 max-lg:tw-w-full'}
                                                style={{
                                                    position: 'relative',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        position: 'absolute',
                                                        width: '40px',
                                                        height: '40px',
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    <img className={'img-fluid'} src="/images/key-metrics/sponsor.svg" />
                                                </div>
                                                {Object.keys(sponsorRevenueChartOptions).length > 0 && (
                                                    <Chart
                                                        options={sponsorRevenueChartOptions}
                                                        series={sponsorRevenueChartOptions.series}
                                                        type={'donut'}
                                                        height={200}
                                                    />
                                                )}
                                            </div>
                                            <div className={'tw-w-8/12 max-lg:tw-w-full tw-flex tw-flex-wrap'}>
                                                <ul className={'tw-flex tw-flex-col tw-gap-1 tw-text-xs'}>
                                                    <li className={'tw-flex tw-flex-col'}>
                                                        <span className={'tw-w-full tw-font-semibold'}>{$t('Total Sponsor Revenue')}</span>
                                                        <span className={'tw-w-full inbud-numeric-text'}>
                                                            {formatToFixedMoneyWithLocale(props.summaryRevenue.sponsorship.total_revenue)}
                                                        </span>
                                                    </li>
                                                    <li className={'tw-flex tw-flex-col'}>
                                                        <span className={'tw-w-full tw-font-semibold'}>{$t('Cash')}</span>
                                                        <span className={'tw-w-full inbud-numeric-text'}>
                                                            {formatToFixedLocaleString(props.summaryRevenue.sponsorship.percentage_cash_revenue)}%
                                                        </span>
                                                    </li>
                                                    <li className={'tw-flex tw-flex-col'}>
                                                        <span className={'tw-w-full tw-font-semibold'}>{$t('VIK')}</span>
                                                        <span className={'tw-w-full inbud-numeric-text'}>
                                                            {formatToFixedLocaleString(props.summaryRevenue.sponsorship.percentage_vik_revenue)}%
                                                        </span>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="tw-rounded-lg tw-border-2 tw-border-gray-200 tw-w-3/12 max-lg:tw-w-full">
                                    <div className={'tw-p-2.5 tw-flex'}>
                                        <div className={'tw-flex max-lg:tw-flex-col tw-w-full tw-gap-3'}>
                                            <div className={'tw-w-full tw-flex tw-flex-wrap'}>
                                                <ul className={'tw-flex tw-flex-col tw-gap-1 tw-p-3 tw-w-full tw-text-xs'}>
                                                    <li className={'tw-flex max-lg:tw-flex-col'}>
                                                        <span className={'tw-w-7/12 max-lg:tw-w-full tw-font-semibold'}>{$t('Number of sponsors')}</span>
                                                        <span className={'tw-w-5/12 max-lg:tw-w-full inbud-numeric-text'}>
                                                            {props.summaryRevenue.sponsorship.number_sponsors}
                                                        </span>
                                                    </li>
                                                    <li className={'tw-flex max-lg:tw-flex-col'}>
                                                        <span className={'tw-w-7/12 max-lg:tw-w-full tw-font-semibold'}>{$t('Signed')}</span>
                                                        <span className={'tw-w-5/12 max-lg:tw-w-full inbud-numeric-text'}>
                                                            {props.summaryRevenue.sponsorship.number_signed_sponsors}
                                                        </span>
                                                    </li>
                                                    <li className={'tw-flex max-lg:tw-flex-col'}>
                                                        <span className={'tw-w-7/12 max-lg:tw-w-full tw-font-semibold'}>{$t('Unsigned')}</span>
                                                        <span className={'tw-w-5/12 max-lg:tw-w-full inbud-numeric-text'}>
                                                            {props.summaryRevenue.sponsorship.number_unsigned_sponsors}
                                                        </span>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="tw-rounded-lg tw-border-2 tw-border-gray-200 tw-w-5/12 max-lg:tw-w-full">
                                    <div className={'tw-p-2.5 tw-flex'}>
                                        <table className="tw-table-auto tw-w-full tw-text-xs">
                                            <tbody>
                                                <tr className={'tw-text-blue-75'}>
                                                    <td className={'tw-border-r-2 tw-font-semibold'}>{$t('Signed Sponsors')}</td>
                                                    <td className={'tw-border-r-2 tw-pl-3'}>{$t('Cash')}</td>
                                                    <td className={'tw-border-r-2 tw-pl-3'}>{$t('VIK')}</td>
                                                    <td className={'tw-pl-3'}>{$t('Total')}</td>
                                                </tr>
                                                {props.summaryRevenue.sponsorship.signed_sponsors.map((sponsor) => {
                                                    return (
                                                        <tr key={'signed_sponsors_' + sponsor.name}>
                                                            <td className={'tw-border-r-2 tw-px-3 tw-font-semibold'}>{sponsor.name}</td>
                                                            <td className={'tw-border-r-2 tw-px-3 inbud-numeric-text'}>
                                                                {formatSeparateByThousands(JSON.parse(sponsor.cashflow).cash)}
                                                            </td>
                                                            <td className={'tw-border-r-2 tw-px-3 inbud-numeric-text'}>
                                                                {formatSeparateByThousands(JSON.parse(sponsor.cashflow).vik)}
                                                            </td>
                                                            <td className={'tw-px-3 inbud-numeric-text'}>
                                                                {formatSeparateByThousands(JSON.parse(sponsor.cashflow).total)}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
